import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Box,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import ImagenPerfil from "../../../assets/ImagenPerfilTransmex.png";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../../Generic/CrudRequest";
import { UserEdit, UserAdd } from "./UserAddOrEdit";
import Alerts from "../../../Utils/Alerts";
import Icons from "../../../Utils/Icons";
import EncryptionUtils from "../../../Utils/EncryptionUtils";


export default function AddOrEditUser({ config }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(t("users.add"));
  const [userData, setUserData] = useState(new UserAdd());
  const [selectedLanguage, setSelectedLanguage] = useState(1);
  const [userPhoto, setUserPhoto] = useState("");
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
 //clientes
  const [clientes, setclientes] = useState([]);
  const [selectedOption, setSelectedOption] = useState([]);
  //Empresa
  const [empresas, setEmpresas] = useState([]);
  const [selectedOption1, setSelectedOption1] = useState([]);

  useEffect(() => {
    if (location.pathname.includes("/Usuarios/Editar")) {
      setTitle(t("users.edit"));
      const userId = getUserIdFromPath(location.pathname);
      getUserById(userId);
    } else if (location.pathname.includes("/Usuarios/Agregar")) {
      setTitle(t("users.add"));
      setUserPhoto(ImagenPerfil);
    }

    fetchRoles();
    fetchClientes();
    fetchEmpresas();
  }, [location.pathname, t]);



  useEffect(() => {
    if (location.pathname.includes("/Usuarios/Agregar") && empresas.length > 0) {
      // Al crear, usar la empresa del usuario logueado desde localStorage
      const empresaId = localStorage.getItem("empresa");
      // console.log('Empresa ID desde localStorage:', empresaId);
      
      if (empresaId) {
        const empresaUsuario = empresas.find((e) => e.idEmpresa === parseInt(empresaId));
        // console.log('Empresa encontrada:', empresaUsuario);
        
        if (empresaUsuario) {
          setSelectedOption1(empresaUsuario);
          setUserData(prevData => ({
            ...prevData,
            idEmpresa: parseInt(empresaId)
          }));
        }
      }
    } else if (location.pathname.includes("/Usuarios/Editar")) {
      // Al editar, usar la empresa del usuario que se está editando
      setSelectedOption1(userData.idEmpresa
        ? empresas.find((e) => e.idEmpresa === userData.idEmpresa)
        : {idEmpresa: 0, nombreEmpresa: t("empresa.placeholder")});
    }
  }, [empresas, i18n, t, userData.idEmpresa, location.pathname])

  const getUserIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getUserById = async (userId) => {
    const dataUser = await GET(`${config.url}/api/User/GetUserById/${userId}`);
    setSelectedLanguage(dataUser.idLanguaje);
    setSelectedOption(dataUser.usuarioClientes);
    setUserData(new UserEdit(dataUser));
    
    findImage(dataUser.foto);

  };

  const fetchRoles = async () => {
    const roles = await GET(`${config.url}/api/Roles/Roles`);
    setRoles(roles);
  };

  const findImage = (image) => {
    if (image !== "") {
      const imagePath = `${config.url}/${config.paths.userImage}/${image}`;
      setUserPhoto(imagePath);
    } else {
      setUserPhoto(ImagenPerfil);
    }
  };

  const addUser = async () => {
    const requiredFields = [
      "email",
      "nombre",
      "apellidoPaterno",
      "idLanguaje",
      "rolesIds",
      "idEmpresa",
      "password",
      "repetirPassword",
    ];
    const newErrors = {};

    if (userData.email !== "" && !validateEmail(userData.email)) {
      newErrors.email = t("users.invalidEmail");
    }

    requiredFields.forEach((field) => {
      if (!userData[field]) {
        newErrors[field] = t("users.requiredField");
      }
    });

    if(userData["idEmpresa"] === 0){
        newErrors["idEmpresa"] = t("users.requiredField");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (userData.password !== userData.repetirPassword) {
      handleShowAlert(t("users.passwordsDifferent"), "error");
      return;
    }

    const result = await POST(`${config.url}/api/User/Register`, userData);
    navigate("/Usuarios");
  };

  const editUser = async () => {
    const requiredFields = [
      "email",
      "nombre",
      "apellidoPaterno",
      "idLanguaje",
      "idEmpresa", 
      "rolesIds",
    ];
    const newErrors = {};

    if (userData.email !== "" && !validateEmail(userData.email)) {
      newErrors.email = t("users.invalidEmail");
    }

    requiredFields.forEach((field) => {
      if (!userData[field]) {
        newErrors[field] = t("users.requiredField");
      }
    });

    if(userData["idEmpresa"] === 0){
      newErrors["idEmpresa"] = t("users.requiredField");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const result = await PUT(`${config.url}/api/User`, userData);
    
    // Si el usuario editado es el usuario actual logueado, actualizar localStorage
    const currentUser = JSON.parse(EncryptionUtils.getDecryptedData("user"));
    if (currentUser && currentUser.userId === userData.usuarioId && userData.idEmpresa) {
      localStorage.setItem("empresa", userData.idEmpresa.toString());
      // console.log('Empresa actualizada en localStorage:', userData.idEmpresa);
    }
    
    navigate("/Usuarios");
  };

  const handleSubmit = async () => {
    if (title === t("users.add")) {
      await addUser();
    } else if (title === t("users.edit")) {
      await editUser();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });

    if (name === "password" && value.trim() !== "") {
      if (!validatePassword(value)) {
        setErrors({
          ...errors,
          [name]: t("users.invalidPassword"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }

    if (name === "repetirPassword" && value.trim() !== "") {
      if (!validatePassword(value)) {
        setErrors({
          ...errors,
          [name]: t("users.invalidPassword"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }

    if (name === "email" && value.trim() !== "") {
      if (!validateEmail(value)) {
        setErrors({
          ...errors,
          [name]: t("users.invalidEmail"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }
  };

  const validatePassword = (password) => {
    // Expresión regular para verificar al menos una letra y un número
    const pattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&.,])[A-Za-z\d@$!%*?&.,]{8,}$/;
    return pattern.test(password);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChangeLanguage = (event) => {
    setSelectedLanguage(event.target.value);
    setUserData({
      ...userData,
      idLanguaje: event.target.value,
    });
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        handleShowAlert(t("users.onlyImagesAllowed"), "warning");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setUserPhoto(reader.result);
        const base64Image = reader.result.split(",")[1];
        const extension = file.name.split(".").pop();

        setUserData({
          ...userData,
          base64: base64Image,
          extension: extension,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCheckboxChange = () => {
    setUserData({
      ...userData,
      checkBoxApp: !userData.checkBoxApp,
    });
  };

  const fetchEmployees = async (searchText) => {
    const responseEmployes = await GET(
      `${config.url}/api/Employes/buscar/${searchText}`
    );
    setEmployees(responseEmployes);
  };

  const handleSearchChange = (event, value) => {
    if (value.trim() !== "") {
      fetchEmployees(value);
    } else {
      setEmployees([]);
    }
  };

  const handleShowAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

   // #region clientes
   const fetchClientes = async () => {
    const Clientes = await GET(
      `${config.url}/api/Client/`
    );
    // console.log(Clientes)
    setclientes(Clientes);
  };

  const isOptionEqualToValue = (option, value) => {
    return option.idCliente === value.idCliente; // Comparar el valor de la opción con el valor seleccionado
  };

  const getOptionLabel = (option) => {
    return option ? option.nombre || "" : ""; // Renderizar solo la descripción como etiqueta de opción
  };

  const handleChangeClientes = (
    event,
    selectedOption
  ) => {
    setUserData((prevState) => ({
      ...prevState,
      usuarioClientes: selectedOption,
    }));

    setSelectedOption(selectedOption); // Actualizar el valor seleccionado
  };
  //#endregion

  // #region Empresa
  const fetchEmpresas = async () => {
    const Empresas = await GET(
      `${config.url}/api/Empresa/`
    );
    setEmpresas([{idEmpresa: 0, nombreEmpresa: t("empresa.placeholder")}, ...Empresas]);
 };

  const handleChangeEmpresas = (
    event,
    selectedOption
  ) => {
    setUserData((prevState) => ({
      ...prevState,
      idEmpresa: selectedOption.idEmpresa,
    }));

    setSelectedOption1(selectedOption);
  };

  const getOptionLabelEmpresa = (option) => {
    return option ? option.nombreEmpresa || "" : ""; // Renderizar solo la descripción como etiqueta de opción
  };

  const isOptionEqualToValueEmpresa = (option, value) => {
    if (!option || !value) {
      return false;
    }
    return option.idEmpresa === value.idEmpresa; // Comparar el valor de la opción con el valor seleccionado
  };
  //#endregion


  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <CardContent
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <Icons
              iconName="ArrowBack"
              fontSize="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                marginRight: 20,
              }}
              to="/Usuarios"
              tooltip={t("buttons.back")}
            />
            <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
              {title === t("users.add") ? t("users.add") : t("users.edit")} {t("users.title")}
            </Typography>
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("users.userInfo")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Grid
                sx={{
                  maxWidth: "calc(100% - 64px)",
                  margin: "auto",
                  marginTop: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Box
                  component="img"
                  sx={{
                    minHeight: 260,
                    maxWidth: 260,
                    borderRadius: "50%",
                    objectFit: "cover",
                    margin: 1,
                  }}
                  alt={t("navigation.profileAlt")}
                  src={userPhoto}
                />
                <input
                  accept="image/*"
                  id="button-file"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handlePhotoChange}
                />
                <label htmlFor="button-file">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{ maxWidth: 260 }}
                  >
                    {t("users.selectPhoto")}
                  </Button>
                </label>
              </Grid>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Grid container spacing={2} sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={employees}
                    noOptionsText={t("users.noOptions")}
                    getOptionLabel={(employee) =>
                      `${employee.noEmpleado} - ${employee.nombre} ${
                        employee.apellidoPaterno ?? ""
                      } ${employee.apellidoMaterno ?? ""}`
                    }
                    value={userData.empleado || null}
                    onChange={(event, value) => {
                      if (value) {
                        setUserData({
                          ...userData,
                          empleado: {
                            idEmpleado: value.idEmpleado,
                            noEmpleado: value.noEmpleado,
                            nombre: value.nombre,
                          },
                        });
                      } else {
                        setUserData({
                          ...userData,
                          empleado: null,
                        });
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option && value && option.idEmpleado === value.idEmpleado
                    }
                    onInputChange={handleSearchChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("users.employee")}
                        variant="standard"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("users.email")}
                    name="email"
                    variant="standard"
                    fullWidth
                    value={userData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("users.name")}
                    name="nombre"
                    variant="standard"
                    fullWidth
                    value={userData.nombre}
                    onChange={handleChange}
                    error={!!errors.nombre}
                    helperText={errors.nombre}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("users.lastName")}
                    name="apellidoPaterno"
                    variant="standard"
                    fullWidth
                    value={userData.apellidoPaterno}
                    onChange={handleChange}
                    error={!!errors.apellidoPaterno}
                    helperText={errors.apellidoPaterno}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("users.motherLastName")}
                    name="apellidoMaterno"
                    variant="standard"
                    fullWidth
                    value={userData.apellidoMaterno}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("users.userName")}
                    name="nombreUsuario"
                    variant="standard"
                    fullWidth
                    value={userData.nombreUsuario}
                    onChange={handleChange}
                    error={!!errors.nombreUsuario}
                    helperText={errors.nombreUsuario}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label={t("users.language")}
                    name="idLanguaje"
                    variant="standard"
                    fullWidth
                    value={selectedLanguage}
                    onChange={handleChangeLanguage}
                    error={!!errors.idLanguaje}
                    helperText={errors.idLanguaje}
                  >
                    <MenuItem value={1}>es-MX</MenuItem>
                    <MenuItem value={2}>en-US</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label={t("users.role")}
                    name="rolesIds"
                    variant="standard"
                    fullWidth
                    value={userData.rolesIds}
                    onChange={handleChange}
                    error={!!errors.rolesIds}
                    helperText={errors.rolesIds}
                  >
                    {roles.map((rol) => (
                      <MenuItem key={rol.id} value={rol.id}>
                        {rol.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={clientes}
                  getOptionLabel={getOptionLabel}
                  value={selectedOption}
                  isOptionEqualToValue={isOptionEqualToValue}
                  onChange={handleChangeClientes}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("users.clients")}
                      variant="standard"
                      error={!!errors.usuarioClientes}
                      helperText={errors.usuarioClientes}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
                <Grid item xs={12} md={3}>
                <Autocomplete
                  options={empresas}
                  getOptionLabel={getOptionLabelEmpresa}
                  value={selectedOption1}
                  isOptionEqualToValue={isOptionEqualToValueEmpresa}
                  onChange={handleChangeEmpresas}
                  readOnly={true}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.6)',
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("empresa.title")}
                      variant="standard"
                      error={!!errors.idEmpresa}
                      helperText={errors.idEmpresa}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
                <Grid item xs={12} md={3}>
                  {title === t("users.add") && (
                    <TextField
                      label={t("users.password")}
                      name="password"
                      variant="standard"
                      type="password"
                      fullWidth
                      onChange={handleChange}
                      value={userData.password}
                      error={!!errors.password}
                      helperText={errors.password}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  {title === t("users.add") && (
                    <TextField
                      label={t("users.repeatPassword")}
                      name="repetirPassword"
                      variant="standard"
                      type="password"
                      fullWidth
                      onChange={handleChange}
                      value={userData.repetirPassword}
                      error={!!errors.repetirPassword}
                      helperText={errors.repetirPassword}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container direction="column" sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={3}>
                  <Typography variant="h6">{t("users.accesses")}</Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={userData.checkBoxApp}
                    onChange={handleCheckboxChange}
                  />
                  <Typography>{t("users.mobileAccess")}</Typography>
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  {t("users.save")}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </>
  );
}
